import { HeaderIcons } from '../../shared/icons';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Header from '../../shared/Header';
import { StandingsSidebar, TimerSection, WaitingSpinner } from '../../shared';
import { BonusLevelHost } from './BonusLevelHost';
import { BonusWinnerRoll } from './BonusWinnerRoll';
import {
  BonusLevelPayload,
  BonusGameType,
  BONUS_INTRO_MS,
  BONUS_HOST_RESULT_FALLBACK_MS,
  BONUS_SUBMIT_ONCE_GAME_TYPES,
  BONUS_INCREMENTAL_SCORE_GAME_TYPES,
} from '../../../constants/bonusConstants';
import { getBonusNoWinnerMessages } from '../../../i18n/helpers';
import { LiveAnswer } from '../../shared/LiveAnswers';
import { getGameName } from '../../../utils/gameUtils';
import { syncPlayersFromLeaderboard } from '../../../utils/leaderboardUtils';
import { buildFallbackBonusResult } from '../../../utils/bonusResultUtils';
import { playBonusWinnerSound } from '../../../utils/sounds';
import '../../../styles/game-layout.css';
import { getRandomPuzzleImageUrl } from '../../../utils/bonusPuzzleImage';
import { useTranslation } from '../../../i18n';

interface BonusHostScreenProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string; isHost?: boolean }>;
    gameState: string;
    hostIsPlaying?: boolean;
    isBonusLevel?: boolean;
    bonusLevelPayload?: BonusLevelPayload | null;
    bonusStartTime?: number;
    isPaused?: boolean;
    bonusLevelResult?: {
      submissions?: Array<{ playerId: string; playerName: string; points: number }>;
      leaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
      bonusGameType?: string;
      questionId?: string;
    } | null;
    settings?: { timerDuration?: number };
  };
  socket: { on: (e: string, cb: (d: any) => void) => void; off: (e: string, cb: (d: any) => void) => void; emit: (event: string, data: any) => void };
  onBackToGameSelection?: () => void;
  onGoHome?: () => void;
}

/**
 * Full host screen during bonus: same layout as GameDisplay (timer + left column + StandingsSidebar).
 * Left column = BonusLevelHost (intro -> game view -> results).
 */
export const BonusHostScreen: React.FC<BonusHostScreenProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  onGoHome,
}) => {
  const { t } = useTranslation();
  const payload = roomState.bonusLevelPayload ?? null;
  /** Stable clock — never Date.now() mid-round or focus/get-room-state races reset the bar. */
  const bonusStartTimeRef = useRef<number | null>(
    typeof roomState.bonusStartTime === 'number' ? roomState.bonusStartTime : null
  );
  const bonusStartFromServerRef = useRef(typeof roomState.bonusStartTime === 'number');
  if (typeof roomState.bonusStartTime === 'number') {
    if (!bonusStartFromServerRef.current) {
      bonusStartTimeRef.current = roomState.bonusStartTime;
      bonusStartFromServerRef.current = true;
    } else if (
      bonusStartTimeRef.current != null &&
      Math.abs(roomState.bonusStartTime - bonusStartTimeRef.current) > 500
    ) {
      // Server rewrote the clock after pause/resume — stay aligned with BE.
      bonusStartTimeRef.current = roomState.bonusStartTime;
    }
  } else if (payload?.questionId && bonusStartTimeRef.current == null) {
    bonusStartTimeRef.current = Date.now();
  }
  const bonusStartTime = bonusStartTimeRef.current ?? roomState.bonusStartTime ?? Date.now();
  const timeLimit = payload?.timeLimit ?? 30000;
  
  const [phase, setPhase] = useState<'intro' | 'playing' | 'results'>('intro');
  const [liveAnswers, setLiveAnswers] = useState<LiveAnswer[]>([]);
  const [letterStormBoard, setLetterStormBoard] = useState<
    Array<{ word: string; claimCount: number; firstPlayerName: string; firstPlayerId?: string; basePoints: number }>
  >([]);
  const [timeRemaining, setTimeRemaining] = useState(timeLimit);
  /** Points per player from bonus-level-result (for green +N display); cleared on new bonus. */
  const [bonusResultPoints, setBonusResultPoints] = useState<Map<string, number>>(new Map());
  /** Winner of this bonus round (top scorer; updated on bonus-points-updated for late submits) */
  const [bonusWinner, setBonusWinner] = useState<{ playerName: string; points: number } | null>(null);
  /** True only after bonus-level-result — avoids "no winner" flash during grace before server totals */
  const [bonusOutcomeReady, setBonusOutcomeReady] = useState(false);
  /** Leaderboard from bonus-level-result (authoritative scores; avoids delay until room-state arrives) */
  const [bonusLeaderboard, setBonusLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }> | null>(null);
  /** Keep payload cached so we can show results even after backend clears it */
  const [cachedPayload, setCachedPayload] = useState<BonusLevelPayload | null>(payload);
  const questionIdRef = useRef<string | undefined>(payload?.questionId);
  const appliedBonusResultQidRef = useRef<string | null>(null);
  /** True when the applied result came from the score-diff fallback (server result may still upgrade it). */
  const appliedBonusResultIsFallbackRef = useRef(false);
  /** Scores at bonus start — host sidebar freezes these during incremental bonuses until results. */
  const preBonusScoresRef = useRef<Map<string, number>>(new Map());
  /** For puzzle bonus: pick local image once during intro and broadcast to room */
  const puzzleImageFetchedRef = useRef<string | undefined>(undefined);

  // Capture pre-bonus baselines as soon as the host bonus screen mounts / a new round starts.
  const capturePreBonusScores = useCallback(() => {
    preBonusScoresRef.current = new Map(
      (roomState.players ?? []).map((p) => [p.id, p.score])
    );
  }, [roomState.players]);

  // Use cached payload if current payload is null (happens when backend clears bonus)
  const displayPayload = payload ?? cachedPayload;

  // Sync payload and reset phase when a NEW bonus round starts (not when payload clears during outro).
  useEffect(() => {
    if (!payload?.questionId) return;
    if (payload.questionId !== questionIdRef.current) {
      questionIdRef.current = payload.questionId;
      bonusStartTimeRef.current =
        typeof roomState.bonusStartTime === 'number' ? roomState.bonusStartTime : null;
      bonusStartFromServerRef.current = typeof roomState.bonusStartTime === 'number';
      puzzleImageFetchedRef.current = undefined;
      setCachedPayload(payload);
      setPhase('intro');
      setLiveAnswers([]);
      setLetterStormBoard([]);
      setBonusResultPoints(new Map());
      setBonusWinner(null);
      setBonusOutcomeReady(false);
      setBonusLeaderboard(null);
      appliedBonusResultQidRef.current = null;
      appliedBonusResultIsFallbackRef.current = false;
      capturePreBonusScores();
      setTimeRemaining(payload?.timeLimit ?? 30000);
    } else {
      setCachedPayload(payload);
      if (payload.bonusGameType === 'letter-storm' && Array.isArray(payload.claimedBoard)) {
        setLetterStormBoard((prev) =>
          payload.claimedBoard!.length < prev.length ? prev : payload.claimedBoard!
        );
      }
      // First mount with payload already set: questionIdRef matches, still need baselines.
      if (preBonusScoresRef.current.size === 0 && roomState.players?.length) {
        capturePreBonusScores();
      }
    }
  }, [payload?.questionId, payload?.timeLimit, payload, roomState.players, roomState.bonusStartTime, capturePreBonusScores]);

  // Puzzle bonus: during intro, pick a random local image and broadcast to room (no API)
  useEffect(() => {
    if (phase !== 'intro' || displayPayload?.bonusGameType !== 'puzzle' || !displayPayload?.questionId) return;
    if (puzzleImageFetchedRef.current === displayPayload.questionId) return;

    puzzleImageFetchedRef.current = displayPayload.questionId;
    const imageUrl = getRandomPuzzleImageUrl();
    if (roomState.roomName && socket?.emit) {
      socket.emit('bonus-puzzle-set-image', { roomName: roomState.roomName, imageUrl });
    }
  }, [phase, displayPayload?.bonusGameType, displayPayload?.questionId, roomState.roomName, socket]);

  // Timer: during intro, show full bar (no countdown). After intro, count down from timeLimit.
  // Backend timer runs for timeLimit + BONUS_INTRO_MS so round ends after intro + timeLimit.
  const totalPlayers = roomState.players.length;
  /** Only submit-once bonuses (server ends when everyone submitted); incremental/timer games never use this shortcut. */
  const bonusType = displayPayload?.bonusGameType as BonusGameType | undefined;
  const isSubmitOnceBonus =
    bonusType != null && BONUS_SUBMIT_ONCE_GAME_TYPES.includes(bonusType);
  const isIncrementalBonus =
    bonusType != null && BONUS_INCREMENTAL_SCORE_GAME_TYPES.includes(bonusType);
  // Match BE getPlayersWhoMustSubmitBonus: casting host does not submit.
  const hostIsPlaying =
    roomState.hostIsPlaying === true ||
    (roomState.players ?? []).some((p) => (p as { isHost?: boolean }).isHost === true);
  const playersWhoMustSubmit = hostIsPlaying
    ? totalPlayers
    : Math.max(0, (roomState.players ?? []).filter((p) => p.id !== roomState.hostId).length);
  const allAnswered =
    isSubmitOnceBonus && playersWhoMustSubmit > 0 && liveAnswers.length >= playersWhoMustSubmit;
  const roundEnded = allAnswered || timeRemaining <= 0;

  useEffect(() => {
    if (allAnswered) {
      setTimeRemaining(0);
      setPhase('results'); // Change to results when all answered
      return;
    }
    if (!payload || phase === 'results') return;
    if (roomState.isPaused) return;
    const roundEndTime = bonusStartTime + BONUS_INTRO_MS + timeLimit;
    const interval = setInterval(() => {
      if (phase === 'intro') {
        // Freeze at full during intro (show full game time)
        setTimeRemaining(timeLimit);
        return;
      }
      // Same formula as player: remaining until round end; when playing this equals game time left
      const remaining = Math.max(0, roundEndTime - Date.now());
      setTimeRemaining(Math.min(timeLimit, remaining));
      if (remaining <= 0) {
        setPhase('results'); // for sidebar shouldUpdateScores when bonus-level-result arrives
      }
    }, 200);
    return () => clearInterval(interval);
  }, [payload, bonusStartTime, timeLimit, phase, allAnswered, roomState.isPaused]);

  const timeSeconds = Math.ceil(timeRemaining / 1000);
  const progress = timeLimit > 0 ? (timeRemaining / timeLimit) * 100 : 0;

  const applyBonusResult = useCallback((data: {
    submissions?: Array<{ playerId: string; playerName: string; points: number }>;
    leaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
    questionId?: string;
  }, options?: { fromFallback?: boolean }) => {
    const currentQid = displayPayload?.questionId ?? payload?.questionId ?? roomState.bonusLevelResult?.questionId;
    if (currentQid && data.questionId && data.questionId !== currentQid) return;
    // Fallback must not lock out the real server result; server result may upgrade a fallback.
    if (
      data.questionId &&
      appliedBonusResultQidRef.current === data.questionId &&
      !(appliedBonusResultIsFallbackRef.current && !options?.fromFallback)
    ) {
      return;
    }
    const upgradingFallback =
      !!data.questionId &&
      appliedBonusResultIsFallbackRef.current &&
      !options?.fromFallback;
    if (data.questionId) appliedBonusResultQidRef.current = data.questionId;
    appliedBonusResultIsFallbackRef.current = !!options?.fromFallback;

    setPhase('results');
    setTimeRemaining(0);
    if (data?.submissions?.length) {
      const map = new Map<string, number>();
      data.submissions.forEach((s) => map.set(s.playerId, s.points));
      setBonusResultPoints(map);
      const top = data.submissions[0];
      if (top && top.points > 0) {
        setBonusWinner({ playerName: top.playerName, points: top.points });
        if (!upgradingFallback) {
          playBonusWinnerSound();
        }
      } else {
        setBonusWinner(null);
      }
    } else {
      setBonusResultPoints(new Map());
      setBonusWinner(null);
    }
    if (data?.leaderboard?.length) {
      setBonusLeaderboard(data.leaderboard);
    }
    setBonusOutcomeReady(true);
  }, [displayPayload?.questionId, payload?.questionId, roomState.bonusLevelResult?.questionId]);

  const applyBonusResultRef = useRef(applyBonusResult);
  useEffect(() => {
    applyBonusResultRef.current = applyBonusResult;
  }, [applyBonusResult]);

  // Apply bonus-level-result from global room state (survives socket listener rebind / timing races).
  useEffect(() => {
    if (!roomState.bonusLevelResult) return;
    applyBonusResult(roomState.bonusLevelResult);
  }, [roomState.bonusLevelResult, applyBonusResult]);

  // Recovery: if server bonus timer was lost (DO hibernation), nudge get-room-state to finalize.
  useEffect(() => {
    if (phase !== 'results' || bonusOutcomeReady) return;
    const timer = setTimeout(() => {
      if (roomState.roomName && socket?.emit) {
        socket.emit('get-room-state', { roomName: roomState.roomName });
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [phase, bonusOutcomeReady, roomState.roomName, socket, displayPayload?.questionId]);

  // Fallback: derive winner from live scores when bonus-level-result was missed (stale room-state race / DO wake).
  // Wait longer than server grace so we don't invent deltas from empty pre-bonus baselines.
  useEffect(() => {
    if (phase !== 'results' || bonusOutcomeReady) return;
    const timer = setTimeout(() => {
      if (preBonusScoresRef.current.size === 0) return;
      const players = roomState.players ?? [];
      const fallback = buildFallbackBonusResult(
        players,
        preBonusScoresRef.current,
        displayPayload?.questionId ?? roomState.bonusLevelResult?.questionId
      );
      const hasGains = fallback.submissions.some((s) => s.points > 0);
      if (!hasGains && !roomState.bonusLevelResult) return;
      applyBonusResultRef.current(fallback, { fromFallback: true });
    }, BONUS_HOST_RESULT_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [
    phase,
    bonusOutcomeReady,
    roomState.players,
    roomState.bonusLevelResult,
    displayPayload?.questionId,
  ]);

  // Listen for bonus-level-start and bonus-level-result
  useEffect(() => {
    const onStart = (_data: BonusLevelPayload) => {
      setPhase('intro');
      setLiveAnswers([]);
      setLetterStormBoard([]);
      setBonusResultPoints(new Map());
      setBonusWinner(null);
      setBonusOutcomeReady(false);
      setBonusLeaderboard(null);
      appliedBonusResultQidRef.current = null;
      appliedBonusResultIsFallbackRef.current = false;
      capturePreBonusScores();
    };
    const onTimeUp = () => {
      setPhase('results');
      setTimeRemaining(0); // Game time over, collecting submissions during grace
    };
    const onResult = (data: {
      questionId?: string;
      submissions?: Array<{ playerId: string; playerName: string; points: number }>;
      leaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
    }) => {
      applyBonusResultRef.current(data);
    };
    const onPointsUpdated = (data: {
      submissions?: Array<{ playerId: string; playerName: string; points: number }>;
      leaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
    }) => {
      if (data?.submissions?.length) {
        const map = new Map<string, number>();
        data.submissions.forEach((s) => map.set(s.playerId, s.points));
        setBonusResultPoints(map);
        const top = data.submissions[0];
        if (top && top.points > 0) {
          setBonusWinner({ playerName: top.playerName, points: top.points });
        } else {
          setBonusWinner(null);
        }
      }
      if (data?.leaderboard?.length) setBonusLeaderboard(data.leaderboard);
    };
    const onAnswered = (data: { playerId: string; playerName: string; avatar?: string; answer: string | number; timestamp?: number; questionId?: string }) => {
      setLiveAnswers((prev) => {
        // Only count answers for the current bonus round (ignore stale/trivia questionId)
        const currentBonusQuestionId = displayPayload?.questionId;
        if (currentBonusQuestionId && data.questionId != null && data.questionId !== currentBonusQuestionId) {
          return prev;
        }
        const next = prev.filter((a) => a.playerId !== data.playerId);
        next.push({
          playerId: data.playerId,
          playerName: data.playerName,
          avatar: data.avatar,
          answer: typeof data.answer === 'number' ? String(data.answer) : data.answer,
          timestamp: data.timestamp ?? Date.now(),
        });
        return next;
      });
    };
    const onLetterStormUpdate = (data: {
      questionId?: string;
      board?: Array<{ word: string; claimCount: number; firstPlayerName: string; firstPlayerId?: string; basePoints: number }>;
    }) => {
      const currentBonusQuestionId = displayPayload?.questionId;
      if (currentBonusQuestionId && data.questionId != null && data.questionId !== currentBonusQuestionId) {
        return;
      }
      if (Array.isArray(data?.board)) setLetterStormBoard(data.board);
    };
    socket.on('bonus-level-start', onStart);
    socket.on('bonus-time-up', onTimeUp);
    socket.on('bonus-level-result', onResult);
    socket.on('bonus-points-updated', onPointsUpdated);
    socket.on('player-answered', onAnswered);
    socket.on('letter-storm-update', onLetterStormUpdate);
    return () => {
      socket.off('bonus-level-start', onStart);
      socket.off('bonus-time-up', onTimeUp);
      socket.off('bonus-level-result', onResult);
      socket.off('bonus-points-updated', onPointsUpdated);
      socket.off('player-answered', onAnswered);
      socket.off('letter-storm-update', onLetterStormUpdate);
    };
  }, [socket, displayPayload?.questionId, capturePreBonusScores]);

  const questionNumber = displayPayload?.questionNumber ?? 0;
  const totalQuestions = displayPayload?.totalQuestions ?? 0;

  /** One random "no winner" message per bonus round (stable for the round). */
  const noWinnerMessage = useMemo(
    () => getBonusNoWinnerMessages()[Math.floor(Math.random() * getBonusNoWinnerMessages().length)],
    [displayPayload?.questionId]
  );

  // Use leaderboard from bonus-level-result when available so totals show immediately (no waiting
  // for room-state). Merge avatars from roomState.players. Never lower scores vs room state.
  const standingsPlayers = useMemo(() => {
    if (!bonusLeaderboard?.length) return roomState.players;
    return syncPlayersFromLeaderboard(roomState.players, bonusLeaderboard);
  }, [bonusLeaderboard, roomState.players]);

  /** During incremental bonus play (and tallying), freeze sidebar at pre-bonus totals; animate at results. */
  const sidebarPlayers = useMemo(() => {
    if (!isIncrementalBonus) return standingsPlayers;
    const freezeScores =
      phase === 'playing' || (phase === 'results' && !bonusOutcomeReady);
    if (!freezeScores) return standingsPlayers;
    return standingsPlayers.map((p) => ({
      ...p,
      score: preBonusScoresRef.current.get(p.id) ?? p.score,
    }));
  }, [standingsPlayers, phase, isIncrementalBonus, bonusOutcomeReady]);

  /**
   * Green +N for results: only after bonusOutcomeReady (or explicit server points).
   * Never invent deltas from score−pre during tallying — empty pre baselines made
   * standings show the full standing as "+N" while the winner reel showed the real bonus.
   */
  const sidebarPointsOverride = useMemo(() => {
    if (bonusResultPoints.size > 0) return bonusResultPoints;
    if (!bonusOutcomeReady || phase !== 'results' || !isIncrementalBonus) return undefined;
    if (preBonusScoresRef.current.size === 0) return undefined;
    const map = new Map<string, number>();
    standingsPlayers.forEach((p) => {
      const pre = preBonusScoresRef.current.get(p.id);
      if (pre == null) return;
      const gained = p.score - pre;
      if (gained > 0) map.set(p.id, gained);
    });
    return map.size > 0 ? map : undefined;
  }, [bonusResultPoints, phase, isIncrementalBonus, standingsPlayers, bonusOutcomeReady]);

  const winnerNamePool = useMemo(() => standingsPlayers.map((p) => p.name), [standingsPlayers]);

  const handleRestartGame = () => {
    if (socket?.emit && roomState?.roomName) {
      socket.emit('start-game', { roomName: roomState.roomName });
    }
  };

  return (
    <div className="app" data-testid="bonus-host-screen">
      <Header
        leftButton={{
          icon: HeaderIcons.back,
          onClick: onBackToGameSelection ?? (() => {}),
          tooltip: t('gameplay.backToGameSelection'),
        }}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: handleRestartGame,
          tooltip: t('gameplay.restartGame'),
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div className="header-game-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{t('gameplay.partyHost.gameLabel')}</span>
              <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                {getGameName(roomState.gameType ?? '')}
              </span>
            </div>
            <div className="header-game-name" style={{ width: '1px', height: '24px', backgroundColor: 'var(--border-medium)' }}></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{t('gameplay.partyHost.roomCodeLabel')}</span>
              <div style={{
                fontSize: '1.3rem',
                fontWeight: 'bold',
                letterSpacing: '0.15rem',
                color: 'var(--color-primary)',
                padding: '0.3rem 0.8rem',
                backgroundColor: 'rgba(var(--color-primary-rgb), 0.1)',
                borderRadius: '6px',
                border: '2px solid var(--color-primary)',
              }}>
                {roomState.roomName}
              </div>
            </div>
          </div>
        }
      />
      <div className="game-host-layout" style={{ padding: '1rem' }}>
        <div className="game-host-left">
          <div className="game-host-timer-section">
            <TimerSection
              questionNumber={questionNumber}
              totalQuestions={totalQuestions}
              timeSeconds={timeSeconds}
              progress={progress}
              showQuestionLabel={false}
            />
          </div>
          <div
            className="game-host-question-section"
            style={{
              flex: '1 1 auto',
              display: 'flex',
              flexDirection: 'column',
              // flex-start from the start for puzzle and whos-on-image so title/image don't jump and space stays below for winner
              justifyContent: (phase === 'intro' || phase === 'results' || payload?.bonusGameType === 'puzzle' || payload?.bonusGameType === 'whos-on-image') ? 'flex-start' : 'center',
              alignItems: 'center',
            }}
          >
            <BonusLevelHost
              payload={displayPayload}
              phase={phase}
              onPhaseChange={setPhase}
              bonusStartTime={bonusStartTime}
              allAnswered={allAnswered}
              roundEnded={roundEnded}
              letterStormBoard={letterStormBoard}
            />
            {/* Results: until bonus-level-result, tallying (not "no winner"). Then winner reel or real no-winner copy. */}
            {phase === 'results' && (
              <div
                data-testid="bonus-host-results"
                style={{
                  marginTop: 'auto',
                  paddingBottom: '1.5rem',
                  textAlign: 'center',
                  animation: 'fadeIn 0.5s ease-out',
                  width: '100%',
                }}
              >
                {!bonusOutcomeReady ? (
                  <WaitingSpinner isLoading size="small" message={t('bonus.host.tallyingResults')} />
                ) : bonusWinner ? (
                  <BonusWinnerRoll
                    key={displayPayload?.questionId ?? 'bonus'}
                    target={{ playerName: bonusWinner.playerName, points: bonusWinner.points }}
                    namePool={winnerNamePool}
                  />
                ) : (
                  <>
                    <div className="bonus-host-results-title" style={{ fontSize: 'clamp(1.8rem, 4.5vw, 2.5rem)', marginBottom: '0.5rem', fontWeight: 600 }}>
                      {noWinnerMessage.title}
                    </div>
                    <div className="bonus-host-results-subtitle" style={{ fontSize: 'clamp(1.4rem, 3.5vw, 1.8rem)' }}>
                      {noWinnerMessage.subtitle}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="game-host-right">
          <StandingsSidebar
            players={sidebarPlayers}
            liveAnswers={liveAnswers}
            totalPlayers={roomState.players.length}
            questionId={displayPayload?.questionId}
            shouldUpdateScores={phase === 'results' && bonusOutcomeReady}
            pointsGainedOverride={sidebarPointsOverride}
            theme={{
              primary: 'var(--color-primary)',
              primaryLight: 'rgba(var(--color-primary-rgb), 0.15)',
              accent: 'var(--color-tertiary)',
            }}
          />
        </div>
      </div>
    </div>
  );
};
